import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios, { readProfileUser, restorePortalSession, storeAuthTokens } from '../utils/api';
import './Auth.css';

// Telegram Login Widget типы
declare global {
  interface Window {
    TelegramLoginWidget: {
      dataOnauth: (user: TelegramUser) => void;
    };
  }
}

interface TelegramUser {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
  auth_date: number;
  hash: string;
}

const getHeaderValue = (headers: any, key: string): string | null => {
  if (!headers) return null;
  if (typeof headers.get === 'function') {
    const value = headers.get(key);
    return typeof value === 'string' ? value : null;
  }
  const value = headers[key] ?? headers[key.toLowerCase()] ?? headers[key.toUpperCase()];
  return typeof value === 'string' || typeof value === 'number' ? String(value) : null;
};

const parseRetryAfterSeconds = (headers: any): number | null => {
  const retryAfterRaw = getHeaderValue(headers, 'retry-after');
  if (!retryAfterRaw) return null;

  const numericSeconds = Number(retryAfterRaw);
  if (Number.isFinite(numericSeconds) && numericSeconds > 0) {
    return Math.ceil(numericSeconds);
  }

  const retryDate = Date.parse(retryAfterRaw);
  if (Number.isNaN(retryDate)) return null;

  const deltaSeconds = Math.ceil((retryDate - Date.now()) / 1000);
  return deltaSeconds > 0 ? deltaSeconds : null;
};

export const getLoginErrorMessage = (err: any): string => {
  const status = err?.response?.status;
  const data = err?.response?.data;
  const errorText = typeof data?.error === 'string' ? data.error.trim() : '';
  const messageText = typeof data?.message === 'string' ? data.message.trim() : '';
  const dataText = typeof data === 'string' ? data.trim() : '';

  if (status === 429) {
    const retryAfterSec = parseRetryAfterSeconds(err?.response?.headers);
    if (retryAfterSec) {
      return `Слишком много попыток входа. Повторите через ${retryAfterSec} сек.`;
    }
    return errorText || messageText || dataText || 'Слишком много попыток входа. Повторите позже.';
  }

  if (status === 401) {
    return errorText || messageText || dataText || 'Неверный email или пароль';
  }

  if (status === 403) {
    return errorText || messageText || dataText || 'Доступ к аккаунту ограничен';
  }

  if (typeof status === 'number' && status >= 500) {
    return errorText || messageText || dataText || 'Сервис аутентификации временно недоступен. Попробуйте позже.';
  }

  return errorText || messageText || dataText || 'Ошибка входа';
};

const Login: React.FC = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchParams] = useSearchParams();
  const returnTarget = (() => {
    const raw = searchParams.get('returnUrl');
    if (!raw || raw.startsWith('//') || raw.includes('\\') || Array.from(raw).some((char) => char.charCodeAt(0) < 32)) return '/dashboard';
    try {
      const url = new URL(raw, window.location.origin);
      if (url.origin !== window.location.origin || url.username || url.password
        || url.pathname.startsWith('//')
        || !['http:', 'https:'].includes(url.protocol)
        || /^\/(login|register|__dev_access)(\/|$)/.test(url.pathname)) return '/dashboard';
      return url.pathname + url.search + url.hash;
    } catch { return '/dashboard'; }
  })();
  const [telegramBotUsername, setTelegramBotUsername] = useState('');
  const telegramContainerRef = useRef<HTMLDivElement | null>(null);
  const redirectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (redirectTimer.current !== null) clearTimeout(redirectTimer.current); }, []);

  // Редирект если уже авторизован
  useEffect(() => {
    let disposed = false;
    // Сессия портала из cookie (вход на start.3a-fb.info) тоже считается входом.
    restorePortalSession().then((hasSession) => {
      if (!hasSession || disposed) return;
      // Проверяем валидность токена
      axios.get('/api/auth/profile')
        .then((response) => {
          if (disposed) return;
          // Возвращаем только при распознанном профиле — тем же условием пускает ProtectedRoute.
          if (!readProfileUser(response.data)) {
            setError('Не удалось проверить текущий вход. Ваша сессия сохранена. Повторите попытку позже.');
            return;
          }
          // Полная навигация нужна для возврата в другое приложение портала.
          window.location.replace(returnTarget);
        })
        .catch((err) => {
          if (disposed) return;
          if (err?.response?.status === 401 || err?.response?.status === 403) {
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
          } else {
            setError('Не удалось проверить текущий вход. Ваша сессия сохранена. Повторите попытку позже.');
          }
        });
    });
    return () => { disposed = true; };
  }, [returnTarget]);

  // SSO и OAuth: Проверяем параметры URL
  useEffect(() => {
    const ssoToken = searchParams.get('sso_token');
    const refreshToken = searchParams.get('sso_refresh');
    const oauthSuccess = searchParams.get('oauth_success');
    const emailVerified = searchParams.get('email_verified');
    const errorParam = searchParams.get('error');
    const errorMessage = searchParams.get('message');
    
    // Обработка ошибок
    if (errorParam) {
      const errorMessages: { [key: string]: string } = {
        'oauth_failed': 'Ошибка авторизации через соц сеть',
        'oauth_denied': 'Вы отменили авторизацию',
        'oauth_no_code': 'Не получен код авторизации',
        'invalid_token': 'Недействительная ссылка',
        'verification_failed': errorMessage || 'Ошибка подтверждения email'
      };
      setError(errorMessages[errorParam] || 'Произошла ошибка');
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }
    
    // Успешное подтверждение email
    if (emailVerified === 'true') {
      setSuccess('Email успешно подтвержден! Теперь вы можете войти.');
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }
    
    // SSO токены
    if (ssoToken) {
      localStorage.setItem('token', ssoToken);
      if (refreshToken) {
        localStorage.setItem('refreshToken', refreshToken);
      }
      axios.defaults.headers.common['Authorization'] = `Bearer ${ssoToken}`;
      
      // Убираем токены из URL (безопасность)
      window.history.replaceState({}, document.title, window.location.pathname);
      
      if (oauthSuccess) {
        setSuccess(`Успешный вход через ${oauthSuccess === 'google' ? 'Google' : 'Telegram'}`);
      }
      
      // Перенаправляем на dashboard
      if (redirectTimer.current !== null) clearTimeout(redirectTimer.current);
      redirectTimer.current = setTimeout(() => {
        window.location.replace(returnTarget);
      }, 500);
    }
  }, [searchParams, returnTarget]);

  // Загружаем конфигурацию Telegram
  useEffect(() => {
    const loadTelegramConfig = async () => {
      try {
        const response = await axios.get('/auth/oauth/telegram/config');
        if (response.data.success && response.data.data.bot_username) {
          setTelegramBotUsername(response.data.data.bot_username);
        }
      } catch (err) {
        console.log('Telegram OAuth не настроен');
      }
    };
    loadTelegramConfig();
  }, []);

  // Telegram Login Widget callback
  useEffect(() => {
    window.TelegramLoginWidget = {
      dataOnauth: async (user: TelegramUser) => {
        setLoading(true);
        setError('');
        try {
          const response = await axios.post('/auth/oauth/telegram', user);
          if (response.data.success) {
            localStorage.setItem('token', response.data.data.tokens.accessToken);
            localStorage.setItem('refreshToken', response.data.data.tokens.refreshToken);
            axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.data.tokens.accessToken}`;
            
            setSuccess('Успешный вход через Telegram');
            if (redirectTimer.current !== null) clearTimeout(redirectTimer.current);
            redirectTimer.current = setTimeout(() => {
              window.location.replace(returnTarget);
            }, 500);
          }
        } catch (err: any) {
          setError(err.response?.data?.error || 'Ошибка авторизации через Telegram');
        } finally {
          setLoading(false);
        }
      }
    };
  }, [returnTarget]);

  // Telegram widget injector (рендерим скрипт вручную, т.к. React не выполняет <script> из JSX)
  useEffect(() => {
    if (!telegramBotUsername) return;
    const container = telegramContainerRef.current;
    if (!container) return;

    // Очищаем контейнер перед вставкой
    container.innerHTML = '';

    const script = document.createElement('script');
    script.src = 'https://telegram.org/js/telegram-widget.js?22';
    script.async = true;
    script.setAttribute('data-telegram-login', telegramBotUsername);
    script.setAttribute('data-size', 'large');
    script.setAttribute('data-radius', '8');
    script.setAttribute('data-onauth', 'TelegramLoginWidget.dataOnauth(user)');
    script.setAttribute('data-request-access', 'write');

    container.appendChild(script);

    return () => {
      container.innerHTML = '';
    };
  }, [telegramBotUsername]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await axios.post('/api/auth/login', formData);
      const token = storeAuthTokens(response.data);
      if (!token) {
        setError('Не удалось войти: сервер не вернул токен. Повторите попытку.');
        return;
      }
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      window.location.replace(returnTarget);
    } catch (err: any) {
      setError(getLoginErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    window.location.href = '/auth/oauth/google';
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <div className="container">
      <div className="auth-container">
        <div className="auth-card">
          <h2>Вход в систему</h2>
          <p>Войдите в свой аккаунт для доступа к генератору</p>

          {error && <div className="error">{error}</div>}
          {success && <div className="success">{success}</div>}

          {/* OAuth кнопки */}
          <div className="oauth-buttons">
            <button
              type="button"
              className="btn btn-oauth btn-google"
              onClick={handleGoogleLogin}
              disabled={loading}
            >
              <svg viewBox="0 0 24 24" width="20" height="20">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Войти через Google
            </button>

            {/* Telegram — оставляем только официальный виджет (синяя кнопка) */}
            <div
              className={`telegram-login-wrapper ${telegramBotUsername ? '' : 'disabled'}`}
              ref={telegramContainerRef}
              style={{ marginTop: 8, opacity: telegramBotUsername ? 1 : 0.6 }}
            >
              {!telegramBotUsername && 'Telegram недоступен'}
            </div>
          </div>

          <div className="divider">
            <span>или</span>
          </div>

          <form onSubmit={handleSubmit} className="auth-form">
            <div className="form-group">
              <label className="form-label">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="form-control"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Пароль</label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className="form-control"
                required
              />
            </div>

            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Вход...' : 'Войти'}
            </button>
          </form>

          <div className="auth-footer">
            <p>
              Нет аккаунта? <Link to="/register">Зарегистрироваться</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
